import React, { useEffect, useRef, useState } from 'react';

interface RevealOnScrollProps {
  children: React.ReactNode;
  animation?: string; // e.g., 'animate-fade-in-up', 'animate-slide-in-right'
  duration?: string; // custom duration class if needed
  delay?: number; // delay in seconds
  className?: string; // extra classes for the wrapper
  threshold?: number; // intersection threshold (0 to 1)
  as?: 'div' | 'section' | 'article' | 'span';
}

const RevealOnScroll: React.FC<RevealOnScrollProps> = ({ 
  children, 
  animation = 'animate-fade-in-up', 
  delay = 0, 
  className = '',
  threshold = 0.05,
  as: Component = 'div'
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // If IntersectionObserver is not available or reduced motion preferred, show immediately
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin: '40px 0px', threshold }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [threshold]);

  const style: React.CSSProperties = {
    animationDelay: delay > 0 ? `${delay}s` : undefined,
    animationFillMode: 'both'
  };

  const ElementTag = Component as 'div';

  return (
    <ElementTag
      ref={ref}
      style={style}
      className={`${className} ${isVisible ? animation : 'opacity-0'}`}
    >
      {children}
    </ElementTag>
  );
};

export default RevealOnScroll;